import { format, parseISO } from 'date-fns';

/**
 * Date utility functions for handling local time instead of UTC
 * This prevents timezone issues when creating and displaying dates
 */

/**
 * Creates a new Date object in local time
 * @param {Date|string|number} input - Date input (Date object, ISO string, or timestamp)
 * @returns {Date} Date object in local time
 */
export const createLocalDate = (input) => {
  if (!input) return new Date();
  
  if (input instanceof Date) {
    // If it's already a Date object, create a new one to avoid mutations
    return new Date(input.getTime());
  }
  
  if (typeof input === 'string') {
    // Parse ISO string and create local date
    const date = parseISO(input);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  }
  
  if (typeof input === 'number') {
    // If it's a timestamp, create local date
    const date = new Date(input);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  }
  
  return new Date();
};

/**
 * Creates a date string in YYYY-MM-DD format for local time
 * @param {Date|string|number} input - Date input
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const toLocalDateString = (input) => {
  const localDate = createLocalDate(input);
  return format(localDate, 'yyyy-MM-dd');
};

/**
 * Creates a date string in YYYY-MM-DD format for local time (alias for toLocalDateString)
 * @param {Date|string|number} input - Date input
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const toLocalDate = toLocalDateString;

/**
 * Formats a date for display using local time
 * @param {Date|string|number} input - Date input
 * @param {string} formatString - Format string for date-fns
 * @returns {string} Formatted date string
 */
export const formatLocalDate = (input, formatString = 'MMM dd, yyyy') => {
  const localDate = createLocalDate(input);
  return format(localDate, formatString);
};

/**
 * Creates a date object for today in local time
 * @returns {Date} Today's date in local time
 */
export const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
};

/**
 * Creates a date object for a specific date in local time
 * @param {number} year - Year
 * @param {number} month - Month (0-11, where 0 is January)
 * @param {number} day - Day of month
 * @returns {Date} Date object in local time
 */
export const createDate = (year, month, day) => {
  return new Date(year, month, day, 12, 0, 0, 0);
};

/**
 * Gets the first day of a month in local time
 * @param {Date|string|number} input - Date input
 * @returns {Date} First day of month in local time
 */
export const firstDayOfMonth = (input) => {
  const localDate = createLocalDate(input);
  return new Date(localDate.getFullYear(), localDate.getMonth(), 1, 12, 0, 0, 0);
};

/**
 * Gets the last day of a month in local time
 * @param {Date|string|number} input - Date input
 * @returns {Date} Last day of month in local time
 */
export const lastDayOfMonth = (input) => {
  const localDate = createLocalDate(input);
  return new Date(localDate.getFullYear(), localDate.getMonth() + 1, 0, 12, 0, 0, 0);
};

/**
 * Adds months to a date in local time
 * @param {Date|string|number} input - Date input
 * @param {number} months - Number of months to add (can be negative)
 * @returns {Date} New date in local time
 */
export const addMonths = (input, months) => {
  const localDate = createLocalDate(input);
  const newDate = new Date(localDate);
  newDate.setMonth(newDate.getMonth() + months);
  return new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 12, 0, 0, 0);
};

/**
 * Subtracts months from a date in local time
 * @param {Date|string|number} input - Date input
 * @param {number} months - Number of months to subtract
 * @returns {Date} New date in local time
 */
export const subMonths = (input, months) => {
  return addMonths(input, -months);
};

/**
 * Gets the day of month from a date
 * @param {Date|string|number} input - Date input
 * @returns {number} Day of month (1-31)
 */
export const getDay = (input) => {
  const localDate = createLocalDate(input);
  return localDate.getDate();
};

/**
 * Gets the month from a date
 * @param {Date|string|number} input - Date input
 * @returns {number} Month (0-11, where 0 is January)
 */
export const getMonth = (input) => {
  const localDate = createLocalDate(input);
  return localDate.getMonth();
};

/**
 * Gets the year from a date
 * @param {Date|string|number} input - Date input
 * @returns {number} Year
 */
export const getYear = (input) => {
  const localDate = createLocalDate(input);
  return localDate.getFullYear();
};

/**
 * Checks if two dates are the same day
 * @param {Date|string|number} date1 - First date
 * @param {Date|string|number} date2 - Second date
 * @returns {boolean} True if same day
 */
export const isSameDay = (date1, date2) => {
  const local1 = createLocalDate(date1);
  const local2 = createLocalDate(date2);
  return local1.getFullYear() === local2.getFullYear() &&
         local1.getMonth() === local2.getMonth() &&
         local1.getDate() === local2.getDate();
};

/**
 * Creates a date range for a month in local time
 * @param {Date|string|number} input - Date input
 * @returns {Object} Object with firstDay and lastDay properties
 */
export const getMonthRange = (input) => {
  return {
    firstDay: firstDayOfMonth(input),
    lastDay: lastDayOfMonth(input)
  };
};

/**
 * Formats a date range for display
 * @param {Date|string|number} fromDate - Start date
 * @param {Date|string|number} toDate - End date
 * @param {string} formatString - Format string for date-fns
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (fromDate, toDate, formatString = 'M/d/yyyy') => {
  const localFrom = createLocalDate(fromDate);
  const localTo = createLocalDate(toDate);
  
  if (isSameDay(localFrom, localTo)) {
    return format(localFrom, formatString);
  }
  
  return `${format(localFrom, formatString)} - ${format(localTo, formatString)}`;
};

/**
 * Creates a date for a specific day of month in local time
 * @param {number} dayOfMonth - Day of month (1-31)
 * @param {Date|string|number} baseDate - Base date to get year/month from
 * @returns {Date} Date object in local time
 */
export const createDateForDayOfMonth = (dayOfMonth, baseDate) => {
  const localBase = createLocalDate(baseDate);
  return new Date(localBase.getFullYear(), localBase.getMonth(), dayOfMonth, 12, 0, 0, 0);
};

/**
 * Gets the current date and time in local time
 * @returns {Date} Current date and time in local time
 */
export const now = () => {
  return new Date();
};

/**
 * Creates a date string for API calls (YYYY-MM-DD) ensuring local time
 * @param {Date|string|number} input - Date input
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const toAPIDateString = (input) => {
  return toLocalDateString(input);
};

/**
 * Creates a date string for display (M/d/yyyy) ensuring local time
 * @param {Date|string|number} input - Date input
 * @returns {string} Date string in M/d/yyyy format
 */
export const toDisplayDateString = (input) => {
  return formatLocalDate(input, 'M/d/yyyy');
};

/**
 * Creates a date string for month display (MMMM yyyy) ensuring local time
 * @param {Date|string|number} input - Date input
 * @returns {string} Date string in MMMM yyyy format
 */
export const toMonthYearString = (input) => {
  return formatLocalDate(input, 'MMMM yyyy');
};

/**
 * Creates a date string for short month display (MMM yyyy) ensuring local time
 * @param {Date|string|number} input - Date input
 * @returns {string} Date string in MMM yyyy format
 */
export const toShortMonthYearString = (input) => {
  return formatLocalDate(input, 'MMM yyyy');
};
